import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatApiError, formatBlobApiError } from "@/lib/api";
import { isCoiOnFile } from "@/lib/coi";  // Phase COI-PREVIEW Defect-fix
import { toast } from "sonner";
import { ChevronLeft, ChevronDown, ChevronRight, Send, Upload, ShieldCheck, ShieldAlert, CheckCircle2, XCircle, Truck, AlertTriangle, RefreshCcw, Clock, FileSignature, Receipt } from "lucide-react";
import { formatRateChip } from "@/lib/rentalPricing";
import CreateInvoiceFromRentalModal from "@/components/CreateInvoiceFromRentalModal";
import EditRentalPricingModal from "@/components/EditRentalPricingModal";
import EndRentalModal from "@/components/EndRentalModal";
import RentalCancelModal from "@/components/RentalCancelModal";
import FirstInvoiceCard from "@/components/FirstInvoiceCard";  // Phase FIRST-INV (Part B)
import InspectionCard from "@/components/InspectionCard";
import { CollapsibleSection } from "@/components/CollapsibleSection";  // Fixes A/B — collapsible HISTORY in ACH + COI panels
import DepositCard from "@/components/DepositCard";
import { formatDepositAmount } from "@/lib/deposit";  // Phase DP polish 2
import { paymentMethodLabel } from "@/lib/paymentMethod";  // Phase DP polish 1a
import BillingScheduleCard from "@/components/BillingScheduleCard";
import RentalInvoicesSection from "@/components/RentalInvoicesSection";
// Phase TOLLBILL-P3b → P5d (2026-07-10) — Tolls section on rental detail (bills-first).
import TollBillsList from "@/components/TollBillsList";
import { CoiSendPreviewModal } from "@/components/CoiSendPreviewModal";  // Phase COI-PREVIEW
import CoiStatusChip from "@/components/CoiStatusChip";  // Phase COI-REFRESH (Part B)
import { ReinstateRentalModal } from "@/components/ReinstateRentalModal";  // Phase RENTAL-REINSTATE
import { usePermission } from "@/lib/usePermission";
import InsuranceSnapshotCard from "@/components/InsuranceSnapshotCard";  // Phase INSREQ-IMP
import AlvysStatusChip from "@/components/AlvysStatusChip";  // Phase AL2
// Phase RV — shared "$75,000" formatter for the inline rental-page editor.
import { formatReplacementValue } from "@/components/ReplacementValueInput";
import { useAuth } from "@/context/AuthContext";

const STATUS_LABEL = {
    pending_contract: "Pending contract",
    contract_signed: "Contract signed",
    coi_pending: "Waiting for COI",
    coi_received: "COI received — review",
    coi_approved: "COI approved",
    active: "Active",
    ended: "Ended",
    terminated: "Terminated",
    scheduled: "Scheduled",
    imported: "Imported",
};
const STATUS_COLOR = {
    pending_contract: "bg-slate-100 text-slate-700",
    contract_signed: "bg-blue-50 text-blue-900",
    coi_pending: "bg-amber-50 text-amber-900",
    coi_received: "bg-violet-50 text-violet-800",
    coi_approved: "bg-emerald-50 text-emerald-800",
    active: "bg-emerald-100 text-emerald-900",
    ended: "bg-slate-200 text-slate-700",
    terminated: "bg-red-50 text-red-700",
    imported: "bg-violet-50 text-violet-800",
};

function fmt(iso) { if (!iso) return "—"; try { return new Date(iso).toLocaleString(); } catch { return iso; } }
function fmtDate(iso) { if (!iso) return "—"; return iso.slice(0, 10); }

export default function AdminRentalDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [r, setR] = useState(null);
    const [contract, setContract] = useState(null);
    // Phase ICR — for imported rentals we also load the customer so we
    // can gate the "Send COI request" button on agent_email + agent_name
    // being present. Null for non-imported rentals (we don't fetch).
    const [customer, setCustomer] = useState(null);
    const [busy, setBusy] = useState(false);
    const [showReview, setShowReview] = useState(false);
    const [reviewStatus, setReviewStatus] = useState("approved");
    const [reviewNote, setReviewNote] = useState("");
    const [uploadFile, setUploadFile] = useState(null);
    // Phase ICR — separate input for the imported-rental "Upload existing
    // COI" path so it doesn't clash with the agent-flow file input above.
    const [importedCoiFile, setImportedCoiFile] = useState(null);
    // Phase H.7 — inline-editable equipment replacement value. Stored as a
    // raw digits string in form state; rendered with $ + thousands grouping.
    const [editingValue, setEditingValue] = useState(false);
    const [valueDraft, setValueDraft] = useState("");
    // Phase RENTAL-NOTES-SURFACE (2026-07-09) — surface rental.notes on the
    // detail page. Data was captured (via import-rental modal and rental
    // upsert) but never rendered; admins had no way to see back what they
    // typed. Inline-edit pattern mirrors the "Replacement value" row.
    const [editingNotes, setEditingNotes] = useState(false);
    const [notesDraft, setNotesDraft] = useState("");
    // Phase ACH — paper ACH on file. Single file-picker ref used for
    // both upload and replace; state holds the selected file until the
    // user clicks Save/Upload. Auto-clears after a successful upload.
    const [achFile, setAchFile] = useState(null);
    const [achBusy, setAchBusy] = useState(false);
    // Phase COI-REPLACE — mirrors the ACH file-replace sub-action onto
    // the COI panel. Single state slot holding the picker's choice
    // until the admin clicks Replace. Server archives the prior file
    // into history[] as ``coi_form_replaced``.
    const [coiReplaceFile, setCoiReplaceFile] = useState(null);
    const [coiReplaceBusy, setCoiReplaceBusy] = useState(false);
    // Phase J.8 (2026-08-01) — count of invoices bound to this rental.
    // Drives the release-button gate + backfill warning banner. Refreshed
    // on every ``reload()``.
    const [rentalInvoiceCount, setRentalInvoiceCount] = useState(null);
    // Phase J.8 — surfaced when the server returns 422 no_first_invoice
    // so we can show a shortcut-to-create-invoice modal.
    const [noInvoiceBlockOpen, setNoInvoiceBlockOpen] = useState(false);
    // Phase COIWITHFILE → COI-PREVIEW — single shared preview modal
    // for all COI-send actions (initial resend, imported resend, and
    // the with-file resend that used to be its own confirm dialog).
    // ``coiPreviewMode`` is null when closed; one of "standard_resend"
    // | "imported" | "with_file" when open.
    const [coiPreviewMode, setCoiPreviewMode] = useState(null);
    // Phase COITSEL — inline COI Requirements Template picker.
    // ``coiTemplates``: full active list pulled once on page load.
    // ``coiTemplateId``: current pick (defaults to rental.coi_request_template_id
    //                    or the system default at hydration time).
    const [coiTemplates, setCoiTemplates] = useState([]);
    const [coiTemplateId, setCoiTemplateId] = useState(null);
    // Phase J.2 Pass 2 — Create-invoice wizard.
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [showEditPricing, setShowEditPricing] = useState(false);
    // Phase DP-R1 — End rental modal + 24h reversal countdown.
    const [showEndModal, setShowEndModal] = useState(false);
    // Phase M.1 (2026-08-21) — cancel modal for pre-release rentals.
    const [showCancelModal, setShowCancelModal] = useState(false);
    const CANCEL_ELIGIBLE_STATUSES = new Set([
        "pending_contract", "contract_signed",
        "coi_pending", "coi_received", "coi_approved",
    ]);
    const [undoBusy, setUndoBusy] = useState(false);
    const [nowTick, setNowTick] = useState(() => Date.now());
    // Phase RENTAL-REINSTATE — reinstate modal (no time window).
    const [showReinstateModal, setShowReinstateModal] = useState(false);
    // Phase TASK28E-REASSIGN-PREFILL — reassign-preview modal state.
    const [showReassignModal, setShowReassignModal] = useState(false);
    const [reassignPrefill, setReassignPrefill] = useState(null);
    const [reassignError, setReassignError] = useState(null);
    const [reassignBusy, setReassignBusy] = useState(false);
    const { ready: permsReady, hasPermission } = usePermission();
    const canReinstate = permsReady && hasPermission("Rentals", "Reinstate");
    // Phase TASK28E-REASSIGN-PREFILL — a new rental created off a prior
    // ended/cancelled rental's fields requires the SAME permission the
    // contract-create endpoint gates on (Contracts / Create / draft),
    // since the reassign flow lands on that same endpoint. Hide the
    // button when the caller lacks that permission.
    const canReassign = permsReady && hasPermission("Contracts", "Create / draft");
    useEffect(() => {
        if (r?.status !== "ended" || !r?.reversal_deadline) return;
        const id = setInterval(() => setNowTick(Date.now()), 30_000);
        return () => clearInterval(id);
    }, [r?.status, r?.reversal_deadline]);

    const reload = async () => {
        try {
            const { data } = await api.get(`/admin/rentals/${id}`);
            setR(data);
            if (data.contract_id) {
                const ctr = await api.get(`/admin/contracts/${data.contract_id}`);
                setContract(ctr.data);
            }
            // Phase ICR — pull customer once per load when this rental was
            // imported so the FE knows whether the insurance agent contact
            // is on file (drives the "Send COI request" button gating).
            if (data.imported && data.customer_id) {
                try {
                    const cu = await api.get(`/admin/customers/${data.customer_id}`);
                    setCustomer(cu.data);
                } catch { /* non-fatal; FE just keeps the button disabled */ }
            }
            // Phase COITSEL — fetch the COI template list once per load
            // and pre-select either the rental's stored template or the
            // system default. We tolerate fetch failures (template admin
            // is super-admin-gated; for a plain ``admin`` user the GET
            // call returns 200 thanks to the read widening, but if the
            // tenant has no templates the list is just empty).
            try {
                const tpl = await api.get(`/admin/coi-requirements-templates`);
                const items = tpl.data?.items || [];
                setCoiTemplates(items);
                const stored = data.coi_request_template_id || null;
                const def = items.find((t) => t.is_default);
                const pre = stored
                    || (def && def.id)
                    || (items[0] && items[0].id)
                    || null;
                setCoiTemplateId(pre);
            } catch { /* non-fatal; picker stays empty */ }
            // Phase J.8 (2026-08-01) — fetch invoice count for this
            // rental so we can gate the Release button + surface the
            // "Released without invoice" warning banner when needed.
            try {
                const inv = await api.get("/admin/invoices", {
                    params: { rental_id: id, limit: 1 },
                });
                setRentalInvoiceCount(Number(inv.data?.total || 0));
            } catch {
                // Non-fatal — leave count null; UI treats null as "unknown"
                // and disables the release button conservatively.
                setRentalInvoiceCount(null);
            }
        } catch (err) { toast.error(formatApiError(err)); }
    };
    useEffect(() => {
        // ID changes → reload. setR runs inside ``reload`` to seed the
        // page from the server; this is the canonical data-fetch pattern.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        reload();
    }, [id]);

    if (!r) return <DashboardShell><p className="text-sm text-slate-500">Loading…</p></DashboardShell>;
    const cr = r.coi_request || {};
    // Phase COI-PREVIEW Defect-fix — canonical "COI is on file" signal
    // (see ``lib/coi.js`` for the predicate's rationale).
    const coiOnFile = isCoiOnFile(cr);

    const action = async (path, opts = {}) => {
        setBusy(true);
        try {
            await api.post(`/admin/rentals/${id}${path}`, opts.body || {}, opts.config || {});
            toast.success(opts.label || "Done");
            reload();
        } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
    };

    // Phase B1 follow-up — Release returns the auto-created schedule
    // summary so we can show a rich inline toast with cadence + amount.
    // Phase J.8 (2026-08-01) — when the server rejects with 422
    // ``no_first_invoice`` we surface a targeted modal that offers the
    // shortcut into the invoice-creation flow, rather than a generic
    // toast the operator has to interpret.
    const onRelease = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/rentals/${id}/release`);
            const sched = data && data.auto_created_schedule;
            if (sched && sched.amount_cents) {
                const amt = (sched.amount_cents / 100).toLocaleString("en-US", {
                    minimumFractionDigits: 0, maximumFractionDigits: 2,
                });
                const dayLabel = sched.day_of_month ? `day ${sched.day_of_month}` : sched.cadence_kind;
                toast.success(`Released. Monthly billing of $${amt} starts on ${dayLabel}.`, {
                    duration: 9000,
                    action: {
                        label: "Edit schedule",
                        // Phase B1 follow-up #3 — open the BillingScheduleModal
                        // directly via URL param; BillingScheduleCard picks it
                        // up and opens the edit dialog on mount/update.
                        onClick: () => navigate(`?openSchedule=${sched.id}`, { replace: true }),
                    },
                });
            } else {
                toast.success("Released");
            }
            reload();
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (status === 422 && detail && detail.code === "no_first_invoice") {
                setNoInvoiceBlockOpen(true);
            } else {
                toast.error(formatApiError(err));
            }
        } finally { setBusy(false); }
    };

    const onUpload = async () => {
        if (!uploadFile) { toast.error("Choose a PDF / JPG / PNG first"); return; }
        const fd = new FormData();
        fd.append("file", uploadFile);
        await action("/coi-response", { body: fd, config: { headers: { "Content-Type": "multipart/form-data" } }, label: "COI uploaded" });
        setUploadFile(null);
    };

    // Phase ICR — "Upload existing COI" for imported rentals. Hits the
    // dedicated import endpoint which auto-approves the upload (no agent
    // round-trip, no review step) and does NOT change rental status.
    const onUploadImportedCoi = async () => {
        if (!importedCoiFile) { toast.error("Choose a PDF / JPG / PNG first"); return; }
        const fd = new FormData();
        fd.append("file", importedCoiFile);
        await action("/coi-import-upload", {
            body: fd,
            config: { headers: { "Content-Type": "multipart/form-data" } },
            label: "COI uploaded and approved",
        });
        setImportedCoiFile(null);
    };

    // Phase ICR — "Send COI request" for imported rentals. The button is
    // disabled in the UI when the insurance agent contact is missing, but
    // we also surface the same gating error from the server here just in
    // case the customer record was updated between page-load and click.
    const onSendImportedCoiRequest = async () => {
        setCoiPreviewMode("imported");
    };

    // Phase ICR — view the on-file COI PDF inline (download as blob first
    // so the bearer token reaches the server, then open in a new tab).
    const onViewCoiResponse = async () => {
        try {
            const resp = await api.get(`/admin/rentals/${id}/coi-response/file`, { responseType: "blob" });
            const blob = new Blob([resp.data], { type: resp.headers["content-type"] || "application/pdf" });
            const url = URL.createObjectURL(blob);
            window.open(url, "_blank");
        } catch (err) { toast.error(await formatBlobApiError(err)); }
    };

    // Phase ACH — upload (or replace) the paper ACH form on file. Hits
    // the dedicated admin-only endpoint which archives any prior entry
    // into history[]. Auto-approved on the server side.
    const onUploadAch = async () => {
        if (!achFile) { toast.error("Choose a PDF / JPG / PNG first"); return; }
        setAchBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", achFile);
            await api.post(`/admin/rentals/${id}/ach-import-upload`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("ACH form uploaded");
            setAchFile(null);
            reload();
        } catch (err) { toast.error(formatApiError(err)); } finally { setAchBusy(false); }
    };

    // Phase ACH — view the on-file ACH (admin-only, blob via bearer).
    const onViewAch = async () => {
        try {
            const resp = await api.get(`/admin/rentals/${id}/ach-form/file`, { responseType: "blob" });
            const blob = new Blob([resp.data], { type: resp.headers["content-type"] || "application/pdf" });
            const url = URL.createObjectURL(blob);
            window.open(url, "_blank");
        } catch (err) { toast.error(await formatBlobApiError(err)); }
    };

    // Phase COI-REPLACE — admin replaces the on-file COI with a new
    // paper / scanned upload. Hits the same /coi-import-upload endpoint
    // the initial Phase ICR import uses; backend detects "COI already
    // on file" and archives the prior into history[]. Auto-approved.
    const onUploadCoiReplace = async () => {
        if (!coiReplaceFile) { toast.error("Choose a PDF / JPG / PNG first"); return; }
        setCoiReplaceBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", coiReplaceFile);
            await api.post(`/admin/rentals/${id}/coi-import-upload`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("COI replaced");
            setCoiReplaceFile(null);
            reload();
        } catch (err) { toast.error(formatApiError(err)); } finally { setCoiReplaceBusy(false); }
    };

    const submitReview = async () => {
        if (reviewStatus === "rejected" && !reviewNote.trim()) { toast.error("Note is required when rejecting"); return; }
        setBusy(true);
        try {
            await api.post(`/admin/rentals/${id}/coi-review`, { review_status: reviewStatus, note: reviewNote });
            toast.success(`COI ${reviewStatus}`);
            setShowReview(false); setReviewNote("");
            reload();
        } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
    };

    // Phase H.7 — save the inline-edited equipment replacement value.
    const saveValue = async () => {
        const digits = String(valueDraft || "").replace(/[^0-9]/g, "");
        const n = digits === "" ? null : parseInt(digits, 10);
        if (n !== null && (!Number.isFinite(n) || n < 0 || n > 100_000_000)) {
            toast.error("Value must be a positive integer ≤ 100,000,000");
            return;
        }
        setBusy(true);
        try {
            await api.patch(`/admin/rentals/${id}`, { value: n });
            toast.success("Replacement value saved");
            setEditingValue(false);
            reload();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };

    // Phase RENTAL-NOTES-SURFACE — save handler for the inline Notes editor.
    // Uses the existing PATCH /admin/rentals/{id} endpoint — no new backend.
    const saveNotes = async () => {
        const trimmed = (notesDraft || "").trim();
        setBusy(true);
        try {
            await api.patch(`/admin/rentals/${id}`, { notes: trimmed });
            toast.success("Notes saved");
            setEditingNotes(false);
            reload();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };

    return (
        <DashboardShell>
            <div className="fade-up flex items-start justify-between flex-wrap gap-3">
                <div>
                    <Link to={`/admin/customers/${r.customer_id}`}
                        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-blue-900"
                        data-testid="rental-back-customer"><ChevronLeft size={14} /> Back to customer</Link>
                    <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900 mt-2">RENTAL · {r.id.slice(0, 8)}</p>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" data-testid="rental-title">
                        {r.equipment_type ? r.equipment_type.replace("_", " ").toUpperCase() : "Rental"} {r.unit_number ? `· ${r.unit_number}` : ""}
                    </h1>
                    <div className="flex items-center gap-2 flex-wrap mt-2">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_COLOR[r.status] || "bg-slate-100"}`} data-testid="rental-status">
                            {STATUS_LABEL[r.status] || r.status}
                        </span>
                        {r.imported && (
                            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold bg-violet-50 text-violet-800 ring-1 ring-violet-200"
                                data-testid="rental-imported-badge">Imported</span>
                        )}
                        <span className="text-xs text-slate-500">
                            {fmtDate(r.start_date)} → {fmtDate(r.end_date) || "open"} · {r.make || ""} {r.model || ""} {r.year || ""}
                        </span>
                        {formatRateChip(r) && (
                            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold bg-blue-50 text-blue-900 ring-1 ring-blue-200"
                                data-testid="rental-rate-chip">{formatRateChip(r)}</span>
                        )}
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    {r.status === "coi_received" && (
                        <Button onClick={() => setShowReview(true)} className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                            data-testid="rental-review"><ShieldCheck size={14} /> Review COI</Button>
                    )}
                    {r.status === "coi_approved" && (() => {
                        // Phase J.8 (2026-08-01) — gate the Release button
                        // when we have 0 invoices bound to this rental
                        // (drafts included). Auto-create can still fire
                        // server-side; the FE nudge is UX-first.
                        const noInvoices = rentalInvoiceCount === 0;
                        return (
                            <Button
                                onClick={onRelease}
                                disabled={busy || noInvoices}
                                title={noInvoices
                                    ? "Create the first invoice first — no invoices are bound to this rental"
                                    : undefined}
                                className="bg-emerald-700 hover:bg-emerald-800 gap-1.5"
                                data-testid="rental-release">
                                <Truck size={14} /> Release equipment
                            </Button>
                        );
                    })()}
                    {/* Phase DP-R1 follow-up: the legacy "Terminate" button +
                        /terminate endpoint were removed. The unified "End
                        rental" CTA below is the only path now. */}
                    {r.status === "active" && (
                        <Button variant="outline" onClick={() => setShowEndModal(true)}
                            data-testid="rental-end-open" className="border-red-200 text-red-700 hover:bg-red-50 gap-1.5">
                            <XCircle size={14} /> End rental
                        </Button>
                    )}
                    {/* Phase M.1 (2026-08-21) — cancel button for pre-release
                        rentals (pending_contract / contract_signed / coi_*).
                        Positioned after the primary Review/Release CTAs so
                        it reads as the destructive secondary action. */}
                    {CANCEL_ELIGIBLE_STATUSES.has(r.status) && (
                        <Button
                            variant="outline"
                            onClick={() => setShowCancelModal(true)}
                            data-testid="rental-cancel-btn"
                            className="border-red-200 text-red-700 hover:bg-red-50 gap-1.5"
                        >
                            <XCircle size={14} /> Cancel Rental
                        </Button>
                    )}
                    {/* Phase INV-CAT — header-level "Create invoice"
                        button removed; the equivalent action lives
                        inside the Invoices section below to avoid
                        the duplicate-button confusion testers flagged. */}
                </div>
            </div>

            {r.imported && (
                <div className="mt-6 rounded-md border border-violet-200 bg-violet-50 p-4 flex items-start gap-3"
                    data-testid="rental-imported-banner">
                    <AlertTriangle size={18} className="shrink-0 mt-0.5 text-violet-700" />
                    <div className="flex-1 min-w-0 text-sm">
                        <p className="font-semibold text-violet-900">This rental was imported from a paper contract.</p>
                        <p className="text-violet-900/80 text-xs mt-0.5">Digital signature is not available. The attached paper contract is the source of truth.</p>
                    </div>
                </div>
            )}

            {/* Phase J.8 (2026-08-01) — "Released without invoice" warning
                banner. Fires when the rental is in a possession-implying
                state AND we have zero invoices bound to it. Includes a
                prominent CTA into the backfill invoice flow. */}
            {rentalInvoiceCount === 0
                && ["active", "ended", "terminated", "imported"].includes(r.status)
                && r.released_at && (
                <div className="mt-6 rounded-md border border-red-200 bg-red-50 p-4 flex items-start gap-3"
                    data-testid="rental-no-invoice-banner">
                    <AlertTriangle size={18} className="shrink-0 mt-0.5 text-red-700" />
                    <div className="flex-1 min-w-0 text-sm">
                        <p className="font-semibold text-red-900">
                            This rental was released without an invoice.
                        </p>
                        <p className="text-red-900/80 text-xs mt-0.5">
                            Released {fmtDate(r.released_at)} — no invoice has been created yet.
                            Create a backfill invoice to close the audit gap.
                        </p>
                    </div>
                    <Button
                        size="sm"
                        onClick={() => setShowInvoiceModal(true)}
                        data-testid="rental-create-backfill-invoice-btn"
                        className="bg-red-700 hover:bg-red-800 gap-1.5">
                        <Receipt size={13} /> Create backfill invoice
                    </Button>
                    {/* Phase J.8-F11 (2026-08-03) — super_admin pricing correction. */}
                    {user?.role === "super_admin" && r.status !== "ended" && r.status !== "terminated" && (
                        <Button
                            size="xs"
                            variant="outline"
                            data-testid="edit-rental-pricing-btn"
                            onClick={() => setShowEditPricing(true)}
                            className="gap-1.5">
                            Edit pricing
                        </Button>
                    )}
                </div>
            )}

            {/* Phase DP-R1 — ended state banner + 24h Undo (super_admin only). */}
            {r.status === "ended" && (
                <EndedRentalBanner
                    rental={r}
                    canUndo={user?.role === "super_admin"}
                    nowMs={nowTick}
                    busy={undoBusy}
                    onUndo={async () => {
                        setUndoBusy(true);
                        try {
                            await api.post(`/admin/rentals/${id}/end/reverse`);
                            toast.success("Rental reinstated");
                            reload();
                        } catch (err) { toast.error(formatApiError(err)); } finally { setUndoBusy(false); }
                    }}
                    canReinstate={canReinstate}
                    onOpenReinstate={() => setShowReinstateModal(true)}
                />
            )}

            {/* Phase RENTAL-REINSTATE — modal (always mounted so it can open/close cleanly). */}
            {r.status === "ended" && (
                <ReinstateRentalModal
                    rental={r}
                    open={showReinstateModal}
                    onOpenChange={setShowReinstateModal}
                    onReinstated={reload}
                />
            )}

            {/* Phase TASK28E-REASSIGN-PREFILL — button + modal (visible on ended/cancelled rentals). */}
            {(r.status === "ended" || r.status === "cancelled") && canReassign && (
                <div className="mt-4 flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50/60 px-4 py-3">
                    <div className="text-sm">
                        <p className="font-semibold text-blue-900">Reuse this equipment on a new rental</p>
                        <p className="text-blue-900/70">
                            Copy this rental&apos;s equipment, rate, deposit, and notes into a fresh draft for a different customer.
                        </p>
                    </div>
                    <Button
                        size="sm"
                        onClick={async () => {
                            setReassignBusy(true);
                            setReassignError(null);
                            setReassignPrefill(null);
                            try {
                                const { data } = await api.get(`/admin/rentals/${id}/reassign-prefill`);
                                setReassignPrefill(data);
                            } catch (err) {
                                setReassignError(formatApiError(err));
                            } finally {
                                setReassignBusy(false);
                                setShowReassignModal(true);
                            }
                        }}
                        className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                        data-testid="rental-reassign-open"
                    >
                        <RefreshCcw size={14} /> Reassign to new rental
                    </Button>
                </div>
            )}

            {/* Phase TASK28E-REASSIGN-PREFILL — preview + customer-pick modal. */}
            <Dialog open={showReassignModal} onOpenChange={setShowReassignModal}>
                <DialogContent data-testid="rental-reassign-modal">
                    <DialogHeader>
                        <DialogTitle>Reassign equipment to a new rental</DialogTitle>
                        <DialogDescription>
                            Copies the reusable fields (equipment, rate, deposit, COI template, notes) into a fresh rental draft. You&apos;ll pick the target customer next; all fields stay editable before saving.
                        </DialogDescription>
                    </DialogHeader>
                    {reassignBusy && (
                        <p className="text-sm text-slate-500">Loading source rental…</p>
                    )}
                    {reassignError && (
                        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="rental-reassign-error">
                            {reassignError}
                        </div>
                    )}
                    {reassignPrefill && !reassignError && (
                        <div className="rounded-lg border border-slate-200 bg-white p-3 text-sm space-y-1">
                            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Prefill preview</p>
                            <p><span className="text-slate-500">Equipment:</span> <span className="font-medium">{reassignPrefill.prefill?.equipment_type || "—"}</span></p>
                            <p><span className="text-slate-500">Unit:</span> <span className="font-mono">{reassignPrefill.prefill?.unit_number || "—"}</span></p>
                            <p><span className="text-slate-500">Monthly rate:</span> {reassignPrefill.prefill?.monthly_rate != null ? `$${reassignPrefill.prefill.monthly_rate}` : "—"}</p>
                            <p><span className="text-slate-500">Deposit:</span> {reassignPrefill.prefill?.deposit != null ? `$${reassignPrefill.prefill.deposit}` : "—"}</p>
                            <p className="text-xs text-slate-400 pt-2">
                                Source: {reassignPrefill.source_customer_name} · <span className="capitalize">{reassignPrefill.source_status}</span>
                            </p>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowReassignModal(false)} data-testid="rental-reassign-cancel">
                            Cancel
                        </Button>
                        <Button
                            disabled={!reassignPrefill || reassignBusy}
                            onClick={() => {
                                setShowReassignModal(false);
                                // Navigate to customer picker (list) with the source id as a
                                // query param. The list page can pass it through on the
                                // customer-link click so ContractsTab auto-opens the
                                // create-rental dialog with the prefill hydrated.
                                navigate(`/admin/customers?reassign_from=${id}`);
                            }}
                            className="bg-blue-900 hover:bg-blue-800"
                            data-testid="rental-reassign-pick-customer"
                        >
                            Pick target customer →
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                    <CardContent className="p-5 space-y-3">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                            <p className="text-xs font-medium tracking-[0.18em] uppercase text-slate-500">Equipment</p>
                            {/* Phase AL1 + AL2 — Alvys provenance + live status chip. */}
                            {r.alvys_trailer_id && (
                                <div className="flex items-center gap-1.5 flex-wrap">
                                    <span
                                        data-testid="alvys-synced-pill"
                                        title={
                                            r.alvys_synced_at
                                                ? `Last synced from Alvys: ${new Date(r.alvys_synced_at).toLocaleString()}`
                                                : "Linked to an Alvys trailer record"
                                        }
                                        className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200 px-2 py-0.5 text-[10px] font-medium tracking-wide uppercase"
                                    >
                                        Alvys synced
                                    </span>
                                    <AlvysStatusChip alvysTrailerId={r.alvys_trailer_id} />
                                </div>
                            )}
                        </div>
                        <dl className="grid grid-cols-2 gap-y-2 text-sm">
                            <Term label="Type" value={r.equipment_type || "—"} />
                            <Term label="Unit #" value={r.unit_number || "—"} />
                            <Term label="Make / Model" value={`${r.make || "—"} ${r.model || ""}`} />
                            <Term label="Year" value={r.year || "—"} />
                            <Term label="VIN" value={r.vin || "—"} />
                            <Term label="Plate" value={r.plate || "—"} />
                            {/* Phase AL1 — surfaces equipment_size when present. */}
                            {r.equipment_size && (
                                <Term label="Size" value={r.equipment_size} />
                            )}
                            <Term label="Start" value={fmtDate(r.start_date)} />
                            <Term label="End" value={fmtDate(r.end_date) || "open"} />
                            <Term label="Daily rate" value={r.daily_rate ? `$${r.daily_rate}/day` : "—"} />
                            <Term
                                label="Deposit"
                                value={
                                    <span data-testid="rental-equipment-deposit" className="inline-flex items-center gap-1.5">
                                        <span>{formatDepositAmount(r.deposit)}</span>
                                        {r.deposit && typeof r.deposit === "object" && r.deposit.payment_method && (
                                            <span
                                                className="inline-flex items-center rounded-full bg-slate-100 text-slate-700 ring-1 ring-slate-200 px-1.5 py-0.5 text-[10px] font-semibold"
                                                data-testid="rental-equipment-deposit-method-chip">
                                                {paymentMethodLabel(r.deposit.payment_method)}
                                            </span>
                                        )}
                                    </span>
                                }
                            />
                        </dl>
                        {/* Phase H.7 — replacement value row, inline-editable */}
                        <div className="border-t border-slate-100 pt-3 flex items-start justify-between gap-3" data-testid="rental-value-row">
                            <div className="min-w-0">
                                <p className="text-xs text-slate-500">Replacement value</p>
                                {!editingValue ? (
                                    <button
                                        type="button"
                                        onClick={() => { setValueDraft(r.value != null ? String(r.value) : ""); setEditingValue(true); }}
                                        className={`text-sm font-medium hover:underline ${
                                            r.value != null ? "text-slate-900" : "text-slate-400 italic"
                                        }`}
                                        data-testid="rental-value-display">
                                        {r.value != null && r.value > 0 ? formatReplacementValue(r.value) : "Not set — click to add"}
                                    </button>
                                ) : (
                                    <div className="flex items-center gap-2 mt-1">
                                        <Input
                                            type="text"
                                            inputMode="numeric"
                                            placeholder="$0"
                                            value={
                                                valueDraft
                                                    ? `$${parseInt(String(valueDraft).replace(/[^0-9]/g, "") || "0", 10).toLocaleString("en-US")}`
                                                    : ""
                                            }
                                            onChange={(e) => setValueDraft(String(e.target.value).replace(/[^0-9]/g, ""))}
                                            data-testid="rental-value-input"
                                            className="h-9 w-44"
                                        />
                                        <Button size="sm" onClick={saveValue} disabled={busy}
                                            className="bg-blue-900 hover:bg-blue-800 h-9"
                                            data-testid="rental-value-save">Save</Button>
                                        <Button size="sm" variant="outline" onClick={() => setEditingValue(false)} disabled={busy}
                                            className="h-9" data-testid="rental-value-cancel">Cancel</Button>
                                    </div>
                                )}
                            </div>
                        </div>
                        {/* Phase RENTAL-NOTES-SURFACE (2026-07-09) — surface
                            rental.notes below the Replacement value row. Inline-edit
                            pattern mirrors the value row. Read-only when the rental
                            is in a terminal state; empty state shows "No notes" so
                            admins know the field exists. */}
                        <div className="border-t border-slate-100 pt-3" data-testid="rental-notes-row">
                            <p className="text-xs text-slate-500 mb-1">Notes</p>
                            {!editingNotes ? (
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (r.status === "ended" || r.status === "cancelled") return;
                                        setNotesDraft(r.notes || "");
                                        setEditingNotes(true);
                                    }}
                                    disabled={r.status === "ended" || r.status === "cancelled"}
                                    className={`text-sm w-full text-left whitespace-pre-wrap break-words ${
                                        r.notes
                                            ? "text-slate-800"
                                            : "text-slate-400 italic"
                                    } ${
                                        r.status === "ended" || r.status === "cancelled"
                                            ? "cursor-default"
                                            : "hover:text-blue-900 hover:underline"
                                    }`}
                                    data-testid="rental-notes-display"
                                >
                                    {r.notes || (
                                        r.status === "ended" || r.status === "cancelled"
                                            ? "No notes"
                                            : "No notes — click to add"
                                    )}
                                </button>
                            ) : (
                                <div className="flex flex-col gap-2 mt-1">
                                    <textarea
                                        rows={3}
                                        value={notesDraft}
                                        onChange={(e) => setNotesDraft(e.target.value)}
                                        placeholder="Add notes about this rental — special terms, condition, etc."
                                        data-testid="rental-notes-input"
                                        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    <div className="flex gap-2">
                                        <Button size="sm" onClick={saveNotes} disabled={busy} data-testid="rental-notes-save">
                                            {busy ? "Saving…" : "Save"}
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => setEditingNotes(false)} disabled={busy} data-testid="rental-notes-cancel">
                                            Cancel
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                        {contract && (
                            <p className="text-xs text-slate-500 mt-3 border-t border-slate-100 pt-3">
                                Contract: <Link to={`/admin/contracts/${contract.id}`} className="text-blue-900 hover:underline" data-testid="rental-contract-link">{contract.title}</Link>
                                {" · "}<span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] ${
                                    contract.status === "fully_signed" ? "bg-emerald-50 text-emerald-800" : "bg-slate-100 text-slate-700"
                                }`}>{contract.status}</span>
                            </p>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-5 space-y-3" data-testid="rental-coi-panel">
                        <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-xs font-medium tracking-[0.18em] uppercase text-slate-500">COI Request</p>
                                {/* Phase COI-REFRESH (Part B) — color-coded expiry runway chip. */}
                                <CoiStatusChip rentalId={id} />
                            </div>
                            {cr.has_request_pdf && (
                                <Button asChild variant="outline" size="sm" className="gap-1.5"
                                    onClick={async () => {
                                        try {
                                            const resp = await api.get(`/admin/rentals/${id}/coi-request/file`, { responseType: "blob" });
                                            const blob = new Blob([resp.data], { type: "application/pdf" });
                                            const url = URL.createObjectURL(blob);
                                            window.open(url, "_blank");
                                        } catch (err) { toast.error(await formatBlobApiError(err)); }
                                    }}>
                                    <button data-testid="rental-coi-request-pdf">View request PDF</button>
                                </Button>
                            )}
                        </div>
                        {/* Phase COI-PREVIEW Defect-fix — render the
                            "active" COI panel (banner + Send/Resend +
                            history) whenever ANY of these signals
                            exists, not just ``sent_at`` / ``has_response``.
                            Phase ICR's initial-import path historically
                            didn't always set ``has_response=true``, so
                            older imported rentals with a real COI file
                            on disk (``response_coi_path`` /
                            ``response_coi_filename`` populated) were
                            falling through to the "No COI request yet"
                            copy and hiding the Send button. */}
                        {(cr.sent_at || coiOnFile) ? (
                            <div className="text-sm space-y-1">
                                {/* Phase ICR — imported COI uploaded ad-hoc (no agent request). */}
                                {cr.response_source === "admin_import" && !cr.sent_at && (
                                    <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-900"
                                        data-testid="rental-coi-imported-banner">
                                        <p className="font-medium">
                                            <CheckCircle2 size={12} className="inline -mt-0.5 mr-1" />
                                            Imported COI on file — auto-approved.
                                        </p>
                                        <p className="opacity-90 mt-0.5">No agent request was sent; the COI was uploaded directly.</p>
                                    </div>
                                )}
                                {cr.sent_at && (
                                    <>
                                        <p>Sent to <strong>{cr.sent_to?.agent_email || "(no email)"}</strong> on {fmt(cr.sent_at)}</p>
                                        {cr.sent_to?.agent_name && <p className="text-xs text-slate-500">{cr.sent_to.agent_name} · {cr.sent_to.agent_company || "—"} · {cr.sent_to.agent_phone || ""}</p>}
                                    </>
                                )}

                                {cr.upload_token && (
                                    <div className={`mt-3 rounded-md border px-3 py-2.5 text-xs ${
                                        cr.upload_token_used_at ? "bg-emerald-50 border-emerald-200" : "bg-blue-50 border-blue-200"
                                    }`} data-testid="rental-coi-upload-link-panel">
                                        {cr.upload_token_used_at ? (
                                            <p className="text-emerald-900">
                                                <CheckCircle2 size={12} className="inline -mt-0.5 mr-1" />
                                                Agent upload link was <strong>used on {fmt(cr.upload_token_used_at)}</strong>.
                                            </p>
                                        ) : (
                                            <>
                                                <p className="text-blue-900 font-medium">Agent upload link active
                                                    {cr.upload_token_expires_at && <span className="text-blue-900/70 font-normal"> · expires {fmt(cr.upload_token_expires_at)}</span>}
                                                </p>
                                                <div className="flex gap-2 mt-2 flex-wrap">
                                                    <Button size="sm" variant="outline" className="gap-1.5 h-7 px-2 text-[11px]"
                                                        onClick={async () => {
                                                            try {
                                                                const url = `${window.location.origin}/upload-coi/${cr.upload_token}`;
                                                                await navigator.clipboard.writeText(url);
                                                                toast.success("Agent upload link copied");
                                                            } catch { toast.error("Copy failed"); }
                                                        }}
                                                        data-testid="rental-coi-copy-upload-link">
                                                        <FileSignature size={12} /> Copy link
                                                    </Button>
                                                    <a href={`/upload-coi/${cr.upload_token}`} target="_blank" rel="noreferrer"
                                                        className="text-blue-900 hover:underline text-[11px] self-center"
                                                        data-testid="rental-coi-open-upload-link">Open preview →</a>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}

                                {coiOnFile && (
                                    <div className="text-xs text-emerald-700 mt-2 flex flex-wrap items-center gap-x-2 gap-y-1"
                                        data-testid="rental-coi-response-info">
                                        <span>
                                            COI received {fmt(cr.response_received_at)}: {cr.response_coi_filename}
                                        </span>
                                        {cr.response_source === "agent_upload" && (
                                            <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-900 px-1.5 py-0 text-[10px] font-semibold"
                                                data-testid="rental-coi-agent-uploaded-chip">Agent uploaded</span>
                                        )}
                                        {cr.response_source === "agent_upload" && (
                                            <span className="text-[10px] text-slate-500"
                                                data-testid="rental-coi-source-upload-link">Received via Upload Link</span>
                                        )}
                                        {cr.fulfilled_via === "email_reply" && (
                                            <span className="inline-flex items-center rounded-full bg-sky-100 text-sky-900 px-1.5 py-0 text-[10px] font-semibold"
                                                data-testid="rental-coi-email-reply-chip">Received via Email Reply</span>
                                        )}
                                        {cr.fulfilled_via === "email_reply" && (
                                            <span className="text-[10px] text-slate-600" data-testid="rental-coi-email-reply-meta">
                                                from {cr.fulfilled_agent_email} · {fmt(cr.fulfilled_at)}
                                                {(cr.response_coi_gridfs_ids || []).length > 1 && ` · ${cr.response_coi_gridfs_ids.length} files`}
                                            </span>
                                        )}
                                        {cr.response_source === "admin_import" && (
                                            <span className="inline-flex items-center rounded-full bg-violet-100 text-violet-900 px-1.5 py-0 text-[10px] font-semibold"
                                                data-testid="rental-coi-imported-chip">Imported</span>
                                        )}
                                        {/* Phase ICR — inline view link for the on-file COI doc. */}
                                        <button type="button" onClick={onViewCoiResponse}
                                            data-testid="rental-coi-view-response"
                                            className="text-blue-900 hover:underline">View →</button>
                                    </div>
                                )}
                                {/* Phase COI-REPLACE — mirror ACH "Replace file"
                                    sub-action: lets admins swap the on-file COI
                                    when the carrier sends a refreshed cert.
                                    Prior file is archived into history[] as
                                    ``coi_form_replaced``. Hidden when no COI
                                    is on file (the standard upload paths
                                    handle the no-file case).

                                    Phase COI-HOTFIX (2026-07-08) — also gated
                                    on ``r.imported``. Manually-created
                                    rentals go through the full COI request
                                    → agent-upload flow; only imported rentals
                                    (Alvys sync-in) need the direct admin
                                    replace escape hatch because the request
                                    flow may not have been used at all. */}
                                {coiOnFile && r.imported && (
                                    <div className="mt-2 rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-2"
                                        data-testid="rental-coi-replace-block">
                                        <Label className="text-xs">Replace file</Label>
                                        <div className="flex gap-2 mt-1.5 flex-wrap">
                                            <input
                                                type="file"
                                                accept="application/pdf,image/jpeg,image/png"
                                                onChange={(e) => setCoiReplaceFile(e.target.files?.[0] || null)}
                                                data-testid="rental-coi-replace-file"
                                                className="text-xs"
                                            />
                                            <Button size="sm"
                                                onClick={onUploadCoiReplace}
                                                disabled={coiReplaceBusy || !coiReplaceFile}
                                                className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                                                data-testid="rental-coi-replace-submit">
                                                <Upload size={14} /> Replace
                                            </Button>
                                        </div>
                                        <p className="text-[10px] text-slate-500 mt-1">
                                            Prior file is archived into history.
                                        </p>
                                    </div>
                                )}
                                {cr.agent_notes && (
                                    <p className="text-xs text-slate-600 italic" data-testid="rental-coi-agent-notes">
                                        Agent notes: &ldquo;{cr.agent_notes}&rdquo;
                                    </p>
                                )}
                                {cr.review_status && (
                                    <p className={`text-xs mt-1 ${cr.review_status === "approved" ? "text-emerald-700" : "text-red-700"}`}>
                                        Review: {cr.review_status} {cr.review_note && `— ${cr.review_note}`}
                                    </p>
                                )}
                                {/* Phase COITSEL — inline COI Requirements
                                    Template picker. Applies to both the
                                    standard Resend and the imported Send
                                    flow below. */}
                                <CoiTemplatePicker
                                    templates={coiTemplates}
                                    value={coiTemplateId}
                                    onChange={setCoiTemplateId}
                                />
                                <div className="flex gap-2 mt-3 flex-wrap">
                                    {/* Phase RESEND-DEDUPE — the three resend
                                        variants below are mutually exclusive
                                        by design. The standard button is the
                                        fallback shown only when the rental is
                                        NOT imported-pending (Button 2's case)
                                        AND has no COI on file yet (Button 3's
                                        case). Prior to this gate, imported
                                        rentals in coi_pending state rendered
                                        BOTH the standard and imported buttons
                                        side-by-side (identical label). */}
                                    {(r.status === "coi_pending" || r.status === "coi_received")
                                        && !coiOnFile
                                        && !(r.imported && cr.sent_at && !cr.has_response) && (
                                        <Button variant="outline" size="sm"
                                            onClick={() => setCoiPreviewMode("standard_resend")}
                                            disabled={busy}
                                            data-testid="rental-coi-resend"
                                            className="gap-1.5"><RefreshCcw size={14} /> Resend request</Button>
                                    )}
                                    {/* Phase POLISH-1 #4 — imported rentals in
                                        pending state (already sent, no response
                                        yet) get their own resend that re-fires
                                        through the imported endpoint so the
                                        stored coi_request_template_id is
                                        updated with the current pick.
                                        Phase COI-PREVIEW Fix 1 — always allow
                                        the modal to open so the admin sees the
                                        actionable gate-error reason inline. */}
                                    {r.imported && cr.sent_at && !cr.has_response && (
                                        <Button variant="outline" size="sm"
                                            onClick={onSendImportedCoiRequest}
                                            disabled={busy}
                                            data-testid="rental-coi-imported-resend"
                                            className="gap-1.5"><RefreshCcw size={14} /> Resend request</Button>
                                    )}
                                    {r.imported && cr.sent_at && !cr.has_response && !(r.value != null && r.value > 0) && (
                                        <span className="text-[11px] text-amber-700 self-center"
                                            data-testid="rental-coi-imported-resend-needs-value">
                                            Set replacement value above to enable.
                                        </span>
                                    )}
                                    {r.imported && cr.sent_at && !cr.has_response && (r.value != null && r.value > 0) && !(customer?.agent_email) && (
                                        <span className="text-[11px] text-amber-700 self-center"
                                            data-testid="rental-coi-imported-resend-needs-agent">
                                            Add the customer&apos;s insurance agent contact first.
                                        </span>
                                    )}
                                    {/* Phase COIWITHFILE — new resend button visible when
                                        a COI is already on file/approved. Opens a
                                        confirmation modal because re-firing on a clean
                                        rental should be a deliberate action. Gates +
                                        helper text mirror the standard flow. */}
                                    {coiOnFile && (
                                        <>
                                            <Button variant="outline" size="sm"
                                                onClick={() => setCoiPreviewMode("with_file")}
                                                disabled={busy}
                                                data-testid="rental-coi-resend-with-file"
                                                className="gap-1.5"><RefreshCcw size={14} /> Resend request</Button>
                                            {!(r.value != null && r.value > 0) && (
                                                <span className="text-[11px] text-amber-700 self-center"
                                                    data-testid="rental-coi-resend-with-file-needs-value">
                                                    Replacement value missing — open preview for details.
                                                </span>
                                            )}
                                        </>
                                    )}
                                    {/* Phase RV — helper text right next to the button when the
                                        rental has no replacement value set yet.
                                        Phase RESEND-DEDUPE — mirror the standard
                                        button's exclusion gates so the helper
                                        only accompanies its own button. */}
                                    {(r.status === "coi_pending" || r.status === "coi_received")
                                        && !coiOnFile
                                        && !(r.imported && cr.sent_at && !cr.has_response)
                                        && !(r.value != null && r.value > 0) && (
                                        <span className="text-[11px] text-amber-700 self-center"
                                            data-testid="rental-coi-resend-needs-value">
                                            Set replacement value above to enable.
                                        </span>
                                    )}
                                </div>
                                {/* Standard agent-driven upload — only meaningful while an
                                    agent request is live (i.e. cr.sent_at exists). Hidden
                                    on imported-rental panels where the new ICR controls
                                    below cover the "no agent request" path. */}
                                {cr.sent_at && (
                                    <div className="mt-4">
                                        <Label className="text-xs">Upload received COI (PDF/JPG/PNG)</Label>
                                        <div className="flex gap-2 mt-1.5">
                                            <input type="file" accept="application/pdf,image/jpeg,image/png"
                                                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                                                data-testid="rental-coi-upload-file" className="text-xs" />
                                            <Button size="sm" onClick={onUpload} disabled={busy || !uploadFile}
                                                className="bg-blue-900 hover:bg-blue-800 gap-1.5" data-testid="rental-coi-upload"><Upload size={14} /> Upload</Button>
                                        </div>
                                        <p className="text-[10px] text-slate-500 mt-1">Use this if the agent prefers to email the COI back instead of using the upload link.</p>
                                    </div>
                                )}
                                {cr.history && cr.history.length > 0 && (
                                    <div className="mt-3 border-t border-slate-100 pt-2">
                                        <CollapsibleSection
                                            title={`COI history (${cr.history.length})`}
                                            storageKey="rental_coi_history_collapsed"
                                            defaultCollapsed={true}
                                            testIdPrefix="rental-coi-history">
                                            <ul className="text-xs text-slate-600 space-y-1">
                                                {cr.history.map((h, i) => (
                                                    <li key={i} data-testid={`rental-coi-history-${i}`} className="flex items-center gap-2">
                                                        <Clock size={10} />
                                                        <span>{h.event} · {fmt(h.at)}</span>
                                                        {h.source === "agent_upload" && (
                                                            <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-800 px-1.5 py-0 text-[9px] font-semibold">agent</span>
                                                        )}
                                                        {h.source === "email_reply" && (
                                                            <span className="inline-flex items-center rounded-full bg-sky-50 text-sky-800 px-1.5 py-0 text-[9px] font-semibold">email</span>
                                                        )}
                                                        {h.source === "admin_upload" && (
                                                            <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-700 px-1.5 py-0 text-[9px] font-semibold">admin</span>
                                                        )}
                                                        {h.source === "admin_import" && (
                                                            <span className="inline-flex items-center rounded-full bg-violet-50 text-violet-800 px-1.5 py-0 text-[9px] font-semibold">imported</span>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        </CollapsibleSection>
                                    </div>
                                )}
                            </div>
                        ) : r.imported ? (
                            /* Phase ICR — imported rental with no COI on file yet.
                               Two paths: upload an existing paper COI (auto-approved),
                               or send a fresh request to the insurance agent if
                               their contact is on the customer record. */
                            <ImportedCoiControls
                                customer={customer}
                                rental={r}
                                busy={busy}
                                importedCoiFile={importedCoiFile}
                                setImportedCoiFile={setImportedCoiFile}
                                onUploadImportedCoi={onUploadImportedCoi}
                                onSendImportedCoiRequest={onSendImportedCoiRequest}
                                coiTemplates={coiTemplates}
                                coiTemplateId={coiTemplateId}
                                setCoiTemplateId={setCoiTemplateId}
                            />
                        ) : (
                            <p className="text-sm text-slate-500">No COI request yet — fully sign the contract to auto-fire one.</p>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Phase INSREQ-IMP — Insurance requirements (frozen) card.
                Moved ABOVE the ACH Form on File section per user
                decision 4a. Reads from ``rental.coi_requirements_snapshot``
                (auto-frozen at import; refrozen on COI-send + manual).
                Falls back to the legacy contract-level snapshot for
                pre-Phase-INSREQ-IMP digitally-signed rentals. */}
            <div className="mt-6">
                <InsuranceSnapshotCard
                    rental={r}
                    contract={contract}
                    coiTemplates={coiTemplates}
                    isSuperAdmin={user?.role === "super_admin"}
                    onRefreshed={() => reload()}
                />
            </div>

            {/* Phase ACH — paper / scanned ACH form on file. Distinct from the
                digital ACH that's signed via the rental contract — this is
                admin-only audit material (a scan of a signed paper ACH).
                Lives on every rental, not just imported ones. */}
            <div className="mt-8">
                <AchOnFilePanel
                    rental={r}
                    achFile={achFile}
                    setAchFile={setAchFile}
                    busy={achBusy}
                    onUploadAch={onUploadAch}
                    onViewAch={onViewAch}
                />
            </div>

            {/* Phase INSREQ-IMP — the old inline "Insurance requirements
                (frozen)" block previously lived here; it's now rendered
                ABOVE the ACH section by ``<InsuranceSnapshotCard>``
                (see above). */}

            <Dialog open={showReview} onOpenChange={setShowReview}>
                <DialogContent data-testid="rental-review-dialog">
                    <DialogHeader>
                        <DialogTitle>Review COI</DialogTitle>
                        <DialogDescription>Compare the uploaded COI against the requirements snapshot before deciding.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="space-y-1.5">
                            <Label>Decision</Label>
                            <select value={reviewStatus} onChange={(e) => setReviewStatus(e.target.value)}
                                className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                                data-testid="rental-review-status">
                                <option value="approved">Approve</option>
                                <option value="rejected">Reject</option>
                            </select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Note {reviewStatus === "rejected" && <span className="text-red-500">*</span>}</Label>
                            <Textarea rows={3} value={reviewNote} onChange={(e) => setReviewNote(e.target.value)}
                                placeholder={reviewStatus === "rejected" ? "What needs to be fixed?" : "Optional"}
                                data-testid="rental-review-note" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowReview(false)}>Cancel</Button>
                        <Button onClick={submitReview} disabled={busy} className={reviewStatus === "approved" ? "bg-emerald-700 hover:bg-emerald-800" : "bg-red-600 hover:bg-red-700"}
                            data-testid="rental-review-confirm">{reviewStatus === "approved" ? "Approve COI" : "Reject COI"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Phase J.2 Pass 2 — Create invoice wizard. */}
            <CreateInvoiceFromRentalModal
                open={showInvoiceModal}
                onOpenChange={setShowInvoiceModal}
                rental={r}
                // Phase J.8 — turn on backfill mode when we're rendering
                // in response to the "Released without invoice" warning.
                backfillMode={
                    rentalInvoiceCount === 0
                    && ["active", "ended", "terminated", "imported"].includes(r.status)
                    && !!r.released_at
                }
                onCreated={() => { reload(); }}
            />

            {/* Phase J.8-F11 (2026-08-03) — super_admin pricing correction modal. */}
            <EditRentalPricingModal
                open={showEditPricing}
                onClose={() => setShowEditPricing(false)}
                rental={r}
                onSaved={() => { reload(); }}
            />

            {/* Phase J.8 (2026-08-01) — surfaces when the server returns
                422 ``no_first_invoice`` (auto-create couldn't produce
                an invoice either). Shortcut to open the invoice-create
                modal without hunting for the button below. */}
            <Dialog open={noInvoiceBlockOpen} onOpenChange={setNoInvoiceBlockOpen}>
                <DialogContent data-testid="release-blocked-modal">
                    <DialogHeader>
                        <DialogTitle>Cannot release — create invoice first</DialogTitle>
                        <DialogDescription>
                            This rental has no invoices, and the auto-create attempt
                            didn't produce one (likely the billing schedule or
                            rate isn't set). Create the first invoice manually,
                            then retry the release.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setNoInvoiceBlockOpen(false)}
                            data-testid="release-blocked-close">Close</Button>
                        <Button
                            className="bg-blue-900 hover:bg-blue-800"
                            data-testid="release-blocked-create-invoice"
                            onClick={() => { setNoInvoiceBlockOpen(false); setShowInvoiceModal(true); }}>
                            Create first invoice
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Phase COI-PREVIEW — single shared preview modal absorbs
                the prior Phase COIWITHFILE confirmation dialog. It
                handles the standard resend, the imported resend, and
                the with-file resend (showing the on-file callout in
                that mode). On Send it dispatches to the correct live
                endpoint and reloads the page. */}
            <CoiSendPreviewModal
                open={coiPreviewMode !== null}
                onOpenChange={(next) => { if (!next) setCoiPreviewMode(null); }}
                rental={r}
                customer={customer}
                coiRequest={cr}
                mode={coiPreviewMode || "standard_resend"}
                coiTemplates={coiTemplates}
                initialTemplateId={coiTemplateId}
                onTemplateChange={setCoiTemplateId}
                onSent={reload}
            />

            {/* Phase DP-R1 — End Rental modal. */}
            <EndRentalModal
                open={showEndModal}
                onOpenChange={setShowEndModal}
                rental={r}
                onSuccess={reload}
            />

            {/* Phase M.1 (2026-08-21) — Cancel Rental modal (pre-release only). */}
            <RentalCancelModal
                open={showCancelModal}
                onOpenChange={setShowCancelModal}
                rental={r}
                onCanceled={() => {
                    // Either the cancel succeeded or the rental was
                    // already canceled server-side — either way, refresh
                    // so the FE reflects the terminal state.
                    reload();
                }}
            />

            {/* Phase MGR1 — manager handoff banner */}
            {r.status === "active" && r.return_recorded_at && (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
                    <div
                        data-testid="admin-manager-handoff-banner"
                        className="rounded-xl bg-amber-50 border border-amber-200 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                    >
                        <div className="flex items-start gap-3">
                            <span className="text-2xl" aria-hidden>📥</span>
                            <div>
                                <div className="font-semibold text-amber-900">
                                    Return recorded by {r.return_recorded_by_name || "yard manager"} on {(r.return_recorded_at || "").slice(0, 10)}
                                </div>
                                <div className="text-sm text-amber-800 mt-0.5">
                                    Ready for closure. Complete the End Rental flow to issue the final invoice + deposit reconciliation.
                                </div>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => setShowEndModal(true)}
                            data-testid="admin-complete-end-rental"
                            className="shrink-0 bg-amber-900 hover:bg-amber-800 text-white px-4 py-2 rounded-md text-sm font-semibold whitespace-nowrap"
                        >
                            Complete End Rental →
                        </button>
                    </div>
                </div>
            )}

            {/* Phase DP-R2 — Equipment return inspection card.
                Task 8 Tweak A — ``pt-10`` adds a clear visual gap from the
                ACH Form on File card that immediately precedes it. */}
            {(r.status === "active" || r.status === "ended") && (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4 pt-10">
                    <InspectionCard rental={r} onChange={reload} readOnly={r.status === "ended"} />
                </div>
            )}

            {/* Phase DP-R3 — Security deposit card. */}
            {(r.status === "active" || r.status === "ended") && (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
                    <DepositCard rental={r} currentUser={user} onChange={reload} />
                </div>
            )}


            {/* Phase FIRST-INV (Part B) — manual first-invoice card,
                visible only pre-release once contract is fully signed.
                Phase RENTAL-COLLAPSE — ``pt-6`` restores breathing room
                after the ACH Form on File card. */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4 pt-6">
                <FirstInvoiceCard rental={r} onChange={reload} />
            </div>

            {/* Phase J.3.3 — Recurring billing card (super-admin gets CTAs). */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
                <BillingScheduleCard rentalId={id} role={user?.role || "admin"} />
            </div>

            {/* Phase L — Invoices section (path-aware title). */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
                <RentalInvoicesSection
                    rentalId={id}
                    customerId={r.customer_id}
                    onCreateInvoice={() => setShowInvoiceModal(true)}
                />
            </div>

            {/* Phase TOLLBILL-P5d — Bills section scoped to this rental. */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8" data-testid="rental-tolls-section">
                <div className="mb-3">
                    <h2 className="font-display text-xl font-semibold text-slate-900">Toll bills</h2>
                    <p className="text-sm text-slate-500 mt-0.5">Toll bills associated with this rental, plus their invoicing state.</p>
                </div>
                <TollBillsList
                    rentalId={id}
                    testIdPrefix="rental-tolls"
                    emptyText="No toll bills have been recorded for this rental yet."
                />
            </div>
        </DashboardShell>
    );
}

function Term({ label, value }) {
    return (<><dt className="text-xs text-slate-500">{label}</dt><dd className="text-slate-900">{value}</dd></>);
}

// Phase DP-R1 — ended-state banner with 24h reversal countdown.
// Phase RENTAL-REINSTATE — also renders a permission-gated "Reinstate
// rental" button that opens the ReinstateRentalModal (no time window).
function EndedRentalBanner({ rental, canUndo, nowMs, busy, onUndo, canReinstate, onOpenReinstate }) {
    const deadline = rental.reversal_deadline ? new Date(rental.reversal_deadline).getTime() : 0;
    const msLeft = Math.max(0, deadline - nowMs);
    const undoOpen = msLeft > 0;
    const hours = Math.floor(msLeft / 3_600_000);
    const minutes = Math.floor((msLeft % 3_600_000) / 60_000);
    const decision = rental.terminate_credit_decision || {};
    const finalInvId = rental.final_invoice_id;
    const fmtDateOnly = (iso) => (iso ? String(iso).slice(0, 10) : "—");
    const fmtMoney = (cents) => (Number(cents || 0) / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });
    const creditLabel = (() => {
        if (!decision.unused_amount_cents) return null;
        const amt = fmtMoney(decision.unused_amount_cents);
        if (decision.action === "stripe_refund") return `Unused portion of ${amt} was issued as Stripe refund.`;
        if (decision.action === "credit_memo") return `Unused portion of ${amt} was issued as a credit memo.`;
        return `Unused portion of ${amt} was forfeited.`;
    })();
    return (
        <div className="mt-6 rounded-md border border-slate-300 bg-slate-50 p-4 flex items-start gap-3"
            data-testid="rental-ended-banner">
            <AlertTriangle size={18} className="shrink-0 mt-0.5 text-slate-600" />
            <div className="flex-1 min-w-0 text-sm">
                <p className="font-semibold text-slate-900">
                    This rental ended on {fmtDateOnly(rental.end_date)} ({rental.end_type === "natural" ? "natural end" : "terminated"}).
                    {finalInvId && (
                        <> Final invoice: <Link to={`/admin/invoices/${finalInvId}`}
                            className="text-blue-900 hover:underline ml-1" data-testid="rental-ended-final-invoice-link">
                            view
                        </Link>.</>
                    )}
                    {rental.damages_invoice_id && (
                        <> Damages invoice: <Link to={`/admin/invoices/${rental.damages_invoice_id}`}
                            className="text-blue-900 hover:underline ml-1" data-testid="rental-ended-damages-invoice-link">
                            view
                        </Link>.</>
                    )}
                </p>
                {rental.end_reason && (
                    <p className="text-slate-700 text-xs mt-1"><span className="font-medium">Reason:</span> {rental.end_reason}</p>
                )}
                {creditLabel && (
                    <p className="text-slate-700 text-xs mt-0.5" data-testid="rental-ended-credit-label">{creditLabel}</p>
                )}
                {rental.deposit && rental.deposit.status !== "not_collected" && (
                    <p className="text-slate-700 text-xs mt-0.5" data-testid="rental-ended-deposit-summary">
                        Deposit: <strong>{fmtMoney(rental.deposit.amount_cents)}</strong> collected
                        {(rental.deposit.applied_amount_cents || 0) > 0 && (
                            <>, <strong>{fmtMoney(rental.deposit.applied_amount_cents)}</strong> applied</>
                        )}
                        {(rental.deposit.refund_amount_cents || 0) > 0 && rental.deposit.refund_action && (
                            <>, <strong>{fmtMoney(rental.deposit.refund_amount_cents)}</strong> refunded via {rental.deposit.refund_action.replace("_", " ")}</>
                        )}
                        {rental.deposit.refund_action === "forfeit" && (rental.deposit.refund_amount_cents || 0) === 0
                            && (rental.deposit.amount_cents || 0) > (rental.deposit.applied_amount_cents || 0) && (
                                <>, surplus forfeited</>
                            )}
                        .
                    </p>
                )}
                {canUndo && undoOpen && (
                    <div className="mt-3 flex items-center gap-3 flex-wrap">
                        <Button onClick={onUndo} disabled={busy} size="sm"
                            variant="outline"
                            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 gap-1.5"
                            data-testid="rental-end-undo">
                            <RefreshCcw size={13} /> Undo termination
                        </Button>
                        <span className="text-[11px] text-slate-500"
                            data-testid="rental-end-undo-countdown">
                            Reversal available for {hours}h {minutes}m
                        </span>
                    </div>
                )}
                {!canUndo && undoOpen && (
                    <p className="text-[11px] text-slate-500 mt-2">
                        Reversal window open for {hours}h {minutes}m — super admin only.
                    </p>
                )}
                {/* Phase RENTAL-REINSTATE — always-available "Reinstate rental"
                    action for admins with the permission. Renders regardless
                    of the 24h reversal window (no time limit per phase spec 7A). */}
                {canReinstate && (
                    <div className="mt-3">
                        <Button onClick={onOpenReinstate} disabled={busy} size="sm"
                            variant="outline"
                            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 gap-1.5"
                            data-testid="rental-reinstate-open">
                            <RefreshCcw size={13} /> Reinstate rental
                        </Button>
                        <span className="ml-2 text-[11px] text-slate-500">
                            Clears the end date · does NOT restore refunded deposits
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
}

/**
 * Phase ICR — controls shown on the COI panel for an imported rental that
 * has no COI on file yet. Two side-by-side actions:
 *   1. Upload an existing paper COI → /coi-import-upload (auto-approved)
 *   2. Send a fresh COI request to the insurance agent on the customer
 *      record → /coi-imported-request (gated on agent_name + agent_email)
 * Kept as a sub-component so the parent's render tree stays readable.
 */
function ImportedCoiControls({
    customer, rental, busy, importedCoiFile, setImportedCoiFile,
    onUploadImportedCoi, onSendImportedCoiRequest,
    coiTemplates, coiTemplateId, setCoiTemplateId,
}) {
    const agentEmail = (customer?.agent_email || "").trim();
    const agentName = (customer?.agent_name || "").trim();
    const hasAgent = Boolean(agentEmail && agentName);
    // Phase RV — outbound COI requests require a replacement value on
    // the rental so the COI's physical_damage_limit can be populated.
    const hasValue = Boolean(rental && rental.value != null && rental.value > 0);
    const canSend = hasAgent && hasValue;
    const blockedReason = !hasValue
        ? "Set rental replacement value before sending COI request."
        : !hasAgent
            ? "Add insurance agent contact on customer record to enable."
            : null;

    return (
        <div className="space-y-4" data-testid="rental-coi-imported-controls">
            <p className="text-sm text-slate-600">
                This rental was imported from a paper contract. No COI request has been issued yet — you can upload an existing COI on file, or send a fresh request to the insurance agent.
            </p>

            <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3">
                <Label className="text-xs">Upload existing COI (PDF/JPG/PNG)</Label>
                <div className="flex gap-2 mt-1.5">
                    <input
                        type="file"
                        accept="application/pdf,image/jpeg,image/png"
                        onChange={(e) => setImportedCoiFile(e.target.files?.[0] || null)}
                        data-testid="rental-coi-imported-upload-file"
                        className="text-xs"
                    />
                    <Button
                        size="sm"
                        onClick={onUploadImportedCoi}
                        disabled={busy || !importedCoiFile}
                        className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                        data-testid="rental-coi-imported-upload-submit"
                    >
                        <Upload size={14} /> Upload
                    </Button>
                </div>
                <p className="text-[10px] text-slate-500 mt-1.5">
                    Uploaded COI is auto-approved — no agent round-trip, no review step.
                </p>
            </div>

            <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3">
                <Label className="text-xs">Send COI request to insurance agent</Label>
                {/* Phase COITSEL — template picker reused inside the imported
                    controls so the admin can pick before clicking Send. */}
                <div className="mt-1.5">
                    <CoiTemplatePicker
                        templates={coiTemplates}
                        value={coiTemplateId}
                        onChange={setCoiTemplateId}
                        compact
                    />
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-1.5">
                    <Button
                        size="sm"
                        onClick={onSendImportedCoiRequest}
                        disabled={busy || !canSend}
                        title={blockedReason || `Send request to ${agentEmail}`}
                        className="bg-emerald-700 hover:bg-emerald-800 gap-1.5 disabled:opacity-60"
                        data-testid="rental-coi-imported-send-request"
                    >
                        <Send size={14} /> Send request
                    </Button>
                    {canSend ? (
                        <span className="text-[11px] text-slate-500" data-testid="rental-coi-imported-agent-summary">
                            → {agentName} &lt;{agentEmail}&gt;
                        </span>
                    ) : (
                        <span className="text-[11px] text-amber-700" data-testid="rental-coi-imported-no-agent">
                            {blockedReason}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}


/**
 * Phase ACH — paper / scanned ACH form on file panel.
 * Mirrors the imported-COI panel structure: emerald banner + filename +
 * View link + history list when a file is on record; upload form when
 * one isn't. Always available on the rental detail page (not gated on
 * imported status) and admin-only on the API side.
 */
function AchOnFilePanel({ rental, achFile, setAchFile, busy, onUploadAch, onViewAch }) {
    const ach = rental?.ach_form_imported || null;
    // Phase RENTAL-COLLAPSE — default COLLAPSED (empty state clutters the
    // page). Force-expand when there's an active upload staged so the
    // admin can see the file chooser + submit row without a click.
    const KEY = "admin.rentalDetail.achFormOnFileCollapsed";
    const [collapsed, setCollapsed] = useState(() => {
        try { return (localStorage.getItem(KEY) ?? "1") === "1"; }
        catch { return true; }
    });
    const forceOpen = !!achFile;  // guardrail — uploaded file staged
    const effectiveCollapsed = collapsed && !forceOpen;
    const toggle = () => {
        setCollapsed((prev) => {
            const next = !prev;
            try { localStorage.setItem(KEY, next ? "1" : "0"); } catch { /* noop */ }
            return next;
        });
    };
    const onKey = (e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); }
    };
    return (
        <Card>
            <CardContent className="p-5 space-y-3" data-testid="rental-ach-panel">
                <div
                    role="button" tabIndex={0}
                    aria-expanded={!effectiveCollapsed}
                    onClick={toggle} onKeyDown={onKey}
                    className="flex items-start justify-between gap-2 cursor-pointer select-none"
                    data-testid="rental-ach-panel-toggle"
                >
                    <div className="flex items-center gap-2 min-w-0">
                        {effectiveCollapsed ? (
                            <ChevronRight size={16} className="text-slate-500 shrink-0" />
                        ) : (
                            <ChevronDown size={16} className="text-slate-500 shrink-0" />
                        )}
                        <p className="text-xs font-medium tracking-[0.18em] uppercase text-slate-500">ACH Form on File</p>
                    </div>
                    <div
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        className="flex items-center gap-2 shrink-0"
                    >
                        {ach && (
                            <span
                                data-testid="rental-ach-status-chip"
                                className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-900 ring-1 ring-emerald-300 px-2 py-0 text-[10px] font-semibold"
                            >
                                <CheckCircle2 size={10} /> {ach.review_status || "on file"}
                            </span>
                        )}
                    </div>
                </div>

                {!effectiveCollapsed && (ach ? (
                    <div className="text-sm space-y-1" data-testid="rental-ach-on-file">
                        <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-900"
                            data-testid="rental-ach-imported-banner">
                            <p className="font-medium">
                                <ShieldCheck size={12} className="inline -mt-0.5 mr-1" />
                                ACH form on file — {ach.filename || "ach.pdf"}
                            </p>
                            <p className="opacity-90 mt-0.5">
                                Imported {fmt(ach.imported_at)}.
                                {ach.response_source === "admin_import" && (
                                    <span className="ml-1.5 inline-flex items-center rounded-full bg-violet-100 text-violet-900 px-1.5 py-0 text-[10px] font-semibold"
                                        data-testid="rental-ach-imported-chip">Imported</span>
                                )}
                                <button type="button" onClick={onViewAch}
                                    data-testid="rental-ach-view"
                                    className="ml-2 text-blue-900 hover:underline">View →</button>
                            </p>
                        </div>
                        {ach.review_note && (
                            <p className="text-xs text-slate-600 italic" data-testid="rental-ach-note">
                                Note: &ldquo;{ach.review_note}&rdquo;
                            </p>
                        )}

                        <div className="mt-3 border-t border-slate-100 pt-3">
                            <Label className="text-xs">Replace file</Label>
                            <div className="flex gap-2 mt-1.5">
                                <input
                                    type="file"
                                    accept="application/pdf,image/jpeg,image/png"
                                    onChange={(e) => setAchFile(e.target.files?.[0] || null)}
                                    data-testid="rental-ach-replace-file"
                                    className="text-xs"
                                />
                                <Button size="sm" onClick={onUploadAch} disabled={busy || !achFile}
                                    className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                                    data-testid="rental-ach-replace-submit">
                                    <Upload size={14} /> Replace
                                </Button>
                            </div>
                            <p className="text-[10px] text-slate-500 mt-1">
                                Prior file is archived into history.
                            </p>
                        </div>

                        {Array.isArray(ach.history) && ach.history.length > 0 && (
                            <div className="mt-3 border-t border-slate-100 pt-2">
                                <CollapsibleSection
                                    title={`ACH history (${ach.history.length})`}
                                    storageKey="rental_ach_history_collapsed"
                                    defaultCollapsed={true}
                                    testIdPrefix="rental-ach-history">
                                    <ul className="text-xs text-slate-600 space-y-1">
                                        {ach.history.map((h, i) => (
                                            <li key={i} data-testid={`rental-ach-history-${i}`} className="flex items-center gap-2">
                                                <Clock size={10} />
                                                <span>{h.event} · {fmt(h.at)}</span>
                                                {h.filename && (
                                                    <span className="text-slate-400 italic">({h.filename})</span>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </CollapsibleSection>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="space-y-3" data-testid="rental-ach-empty">
                        <p className="text-sm text-slate-600">
                            No paper ACH form on file yet. This is for scanned/paper ACH
                            authorizations (admin-only) — distinct from the digital ACH
                            that&apos;s signed via the contract.
                        </p>
                        <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3">
                            <Label className="text-xs">Upload ACH form (PDF/JPG/PNG)</Label>
                            <div className="flex gap-2 mt-1.5">
                                <input
                                    type="file"
                                    accept="application/pdf,image/jpeg,image/png"
                                    onChange={(e) => setAchFile(e.target.files?.[0] || null)}
                                    data-testid="rental-ach-upload-file"
                                    className="text-xs"
                                />
                                <Button size="sm" onClick={onUploadAch} disabled={busy || !achFile}
                                    className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                                    data-testid="rental-ach-upload-submit">
                                    <Upload size={14} /> Upload ACH form
                                </Button>
                            </div>
                            <p className="text-[10px] text-slate-500 mt-1.5">
                                Stored alongside the rental as a pre-approved, admin-only file.
                            </p>
                        </div>
                    </div>
                ))}
            </CardContent>
        </Card>
    );
}


/**
 * Phase COITSEL — inline COI Requirements Template picker.
 *
 * Single source of truth used in two places on the rental detail page:
 *   1. The standard "Resend COI request" action row (in-panel).
 *   2. The imported-rental "Send COI request" sub-panel.
 *
 * Props
 *  templates    Array  — list of {id, name, is_default}; may be empty
 *  value        string — currently selected template id (controlled)
 *  onChange(id)        — fires on user change; parent persists at send time
 *  compact      bool   — slightly smaller label spacing for tight containers
 */
function CoiTemplatePicker({ templates, value, onChange, compact = false }) {
    const hasDefault = templates.some((t) => t.is_default);
    return (
        <div className={compact ? "space-y-1" : "space-y-1.5 mt-3"} data-testid="rental-coi-template-picker">
            {!compact && <Label className="text-xs">COI Requirements Template</Label>}
            <select
                value={value || ""}
                onChange={(e) => onChange(e.target.value || null)}
                disabled={templates.length === 0}
                data-testid="rental-coi-template-select"
                className="block w-full max-w-md rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
                {templates.length === 0 && (
                    <option value="">(no templates available)</option>
                )}
                {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                        {t.name}{t.is_default ? "  (default)" : ""}
                    </option>
                ))}
            </select>
            {!hasDefault && templates.length > 0 && (
                <p className="text-[11px] text-amber-700 flex items-center gap-1"
                    data-testid="rental-coi-no-default-warning">
                    No default COI template set —{" "}
                    <Link to="/admin/coi-templates" className="underline">set one in Templates admin</Link>.
                </p>
            )}
        </div>
    );
}

